import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import SignatureModal from '../components/SignatureModal';

interface SignatureSession {
  id: string;
  quoteId: string;
  clientId: string;
  clientName: string;
  clientEmail: string;
  expiresAt: string;
  status: 'pending' | 'signed' | 'expired';
  acceptData: {
    insurers: Array<{
      id: string;
      name: string;
      type: string;
      premium: string;
      brokerFee: string;
      managementFee: string;
      insurerQuotationNumber: string;
    }>;
  };
}

interface ProposalData {
  quoteReference: string;
  client: any;
  address: any;
  broker: any;
  propertyData: any;
  householdContents: any[];
  vehicles: any[];
  trailersCaravans: any[];
  watercraft: any[];
  riskItems: any[];
  valueAddedProducts: any[];
  declarationQuestions: any[];
  // Add other fields
}

// Helper functions
const formatCurrency = (value: any) => {
  if (!value && value !== 0) return 'R 0.00';
  const num = typeof value === 'string' ? parseFloat(value) : value;
  return `R ${num.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
};

const formatDate = (dateString: string) => {
  try {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  } catch {
    return dateString;
  }
};

// Map cover type from numeric value to display name
const getCoverTypeDisplay = (value: any): string => {
  if (!value) return '—';
  const coverTypeMap: { [key: string]: string } = {
    '1': 'Comprehensive',
    '2': 'Third Party Fire and Theft',
    '3': 'Third Party Property Damage',
    '4': 'Compulsory Third Party',
    'Comprehensive': 'Comprehensive',
    'Third Party Fire and Theft': 'Third Party Fire and Theft',
    'Third Party Property Damage': 'Third Party Property Damage',
    'Compulsory Third Party': 'Compulsory Third Party'
  };
  return coverTypeMap[value.toString()] || value.toString();
};

// Map no claim bonus from numeric value to display name
const getNoClaimBonusDisplay = (value: any): string => {
  if (!value) return 'None';
  const stringValue = value.toString();
  const noClaimBonusMap: { [key: string]: string } = {
    '0': 'None',
    '1': '12 Months (1 Year)',
    '2': '24 Months (2 Year)',
    '3': '36 Months (3 Year)',
    '4': '48 Months (4 Year)',
    '5': '60 Months (5 Year)',
    '6': '72 Months (6 Year)',
    '7': '84 Months (7 Year)',
    '8': '96 Months (8 Year)',
    '9': '108 Months (9 Year)',
    '10': '120 Months (10 Year)',
    '11': 'More than 120 Months (More than 10 Years)',
    'None': 'None',
    '12 Months (1 Year)': '12 Months (1 Year)',
    '24 Months (2 Year)': '24 Months (2 Year)',
    '36 Months (3 Year)': '36 Months (3 Year)',
    '48 Months (4 Year)': '48 Months (4 Year)',
    '60 Months (5 Year)': '60 Months (5 Year)',
    '72 Months (6 Year)': '72 Months (6 Year)',
    '84 Months (7 Year)': '84 Months (7 Year)',
    '96 Months (8 Year)': '96 Months (8 Year)',
    '108 Months (9 Year)': '108 Months (9 Year)',
    '120 Months (10 Year)': '120 Months (10 Year)',
    '1 Year': '12 Months (1 Year)',
    '2 Year': '24 Months (2 Year)',
    '3 Year': '36 Months (3 Year)',
    '4 Year': '48 Months (4 Year)',
    '5 Year Plus': '60 Months (5 Year)'
  };
  return noClaimBonusMap[stringValue] || stringValue;
};

// Helper to get person name
const getPersonName = (personId: any, clientData: any, personsOfInterest: any[] = []): string => {
  if (!personId) return '—';
  if (typeof personId === 'string' && personId.startsWith('client-')) {
    return `${clientData?.name || ''} ${clientData?.surname || ''}`.trim() || 'Client';
  }
  const person = personsOfInterest.find((p: any) => p.id?.toString() === personId.toString());
  if (person) {
    return `${person.name || ''} ${person.surname || ''}`.trim() || '—';
  }
  return String(personId);
};

// Helper to get driver license display
const getDriverLicenseDisplay = (personId: any, personsOfInterest: any[] = []): string => {
  if (!personId) return '';
  if (typeof personId === 'string' && personId.startsWith('client-')) return '';
  const person = personsOfInterest.find((p: any) => p.id?.toString() === personId.toString());
  if (!person) return '';
  const licenses = person.drivers_license_codes;
  if (!licenses || (Array.isArray(licenses) && licenses.length === 0)) return '';
  if (Array.isArray(licenses)) {
    const licenseTexts = licenses.map((license: any) => {
      if (typeof license === 'object') {
        const code = license.code || '';
        const issueDate = license.dateOfFirstIssue || license.date_of_first_issue || '';
        const expiryDate = license.expiresOn || license.expires_on || '';
        if (issueDate && expiryDate) {
          return `${code} (Issued: ${new Date(issueDate).toLocaleDateString()}, Expires: ${new Date(expiryDate).toLocaleDateString()})`;
        } else if (code) {
          return code;
        }
      } else if (typeof license === 'string') {
        return license;
      }
      return '';
    }).filter(text => text);
    return licenseTexts.length > 0 ? ` - License: ${licenseTexts.join(', ')}` : '';
  }
  if (typeof licenses === 'string') return ` - License: ${licenses}`;
  return '';
};

// Helper to get parking location display
const getParkingLocationDisplay = (value: any): string => {
  if (!value) return '—';
  if (typeof value === 'string') {
    const map: { [key: string]: string } = {
      '1': 'Garage',
      '2': 'Car Park',
      '3': 'Marked parking area',
      '4': 'Undercover parking',
      '5': 'Behind close gates',
      '6': 'House carport',
      '7': 'Security estate carport',
      '8': 'Street',
      '9': 'Driveway',
      '10': 'Security Complex',
      '11': 'Other',
    };
    return map[value] || value;
  }
  return value?.name || value?.description || String(value) || '—';
};

// Helper to get safety features display
const getSafetyFeaturesDisplay = (value: any): string => {
  if (!value) return 'None';
  if (typeof value === 'string') {
    const map: { [key: string]: string } = {
      '1': 'Alarm',
      '2': 'Tracking Device',
      '3': 'Factory Fitted Alarm',
      '4': 'Data Dot',
      '5': 'Immobiliser',
      '6': 'Smash and Grab',
    };
    return map[value] || value;
  }
  if (Array.isArray(value)) {
    return value.map(v => v?.name || v?.description || v).join(', ') || 'None';
  }
  return value?.name || value?.description || String(value) || 'None';
};

const PublicSignaturePage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [session, setSession] = useState<SignatureSession | null>(null);
  const [proposalData, setProposalData] = useState<ProposalData | null>(null);
  const [isSignatureModalOpen, setIsSignatureModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [personsOfInterest, setPersonsOfInterest] = useState<any[]>([]);

  useEffect(() => {
    const verifyToken = async () => {
      try {
        setLoading(true);
        console.log("🔍 Verifying token:", token);
        
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/signature/verify/${token}`
        );

        console.log("✅ Verification response:", response.data);

        if (response.data.success) {
          setSession(response.data.session);
          setProposalData(response.data.proposalData);
          
          // Fetch persons of interest if clientId is available
          if (response.data.session?.clientId) {
            fetchPersonsOfInterest(response.data.session.clientId);
          }
        } else {
          setError(response.data.message || 'Invalid or expired signature link');
        }
      } catch (err: any) {
        console.error('❌ Verification error:', err);
        setError(err.response?.data?.message || 'Failed to verify signature link');
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      verifyToken();
    }
  }, [token]);

  const fetchPersonsOfInterest = async (clientId: string) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/persons-of-interest?clientId=${clientId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPersonsOfInterest(response.data || []);
    } catch (error) {
      console.error('Error fetching persons of interest:', error);
    }
  };

  const handleSignClick = () => {
    setIsSignatureModalOpen(true);
  };

  const handleSignatureSave = async (signatureDataUrl: string) => {
    try {
      setIsSubmitting(true);
      setIsSignatureModalOpen(false);

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/signature/submit/${token}`,
        {
          signature: signatureDataUrl,
          signedAt: new Date().toISOString(),
          clientName: session?.clientName,
          clientEmail: session?.clientEmail,
          quoteId: session?.quoteId,
          clientId: session?.clientId
        },
        { timeout: 30000 }
      );

      if (response.data.success) {
        setSuccessMessage('✅ Document signed successfully! The agent has been notified.');
        setTimeout(() => {
          window.location.href = response.data.redirectUrl || '/signature-complete';
        }, 5000);
      } else {
        throw new Error(response.data.message || 'Failed to submit signature');
      }
    } catch (err: any) {
      console.error('❌ Signature submission error:', err);
      setError(err.response?.data?.message || err.message || 'Failed to submit signature');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mb-4"></div>
          <p className="text-gray-600">Loading your proposal...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Link Error</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <p className="text-sm text-gray-500">Please contact your insurance agent for assistance.</p>
        </div>
      </div>
    );
  }

  if (successMessage) {
    return (
      <div className="min-h-screen bg-green-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Thank You!</h2>
          <p className="text-gray-600 mb-4">{successMessage}</p>
          <p className="text-sm text-gray-500">Redirecting you in a few seconds...</p>
        </div>
      </div>
    );
  }

  const client = proposalData?.client;
  const address = proposalData?.address;
  const vehicles = proposalData?.vehicles || [];

  return (
    <div className="min-h-screen bg-gray-100 py-8 px-4">
      <div className="max-w-5xl mx-auto border-2 border-black bg-white p-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold tracking-widest">INSURANCE PROPOSAL FORM</h1>
        </div>

        {/* Quote Number & Inception Date */}
        <div className="grid grid-cols-2 gap-12 mb-10">
          <div>
            <div className="text-sm mb-1">Quote Number:</div>
            <div className="border-b border-black pb-1 font-medium">{session?.quoteId}</div>
          </div>
          <div>
            <div className="text-sm mb-1">Inception Date:</div>
            <div className="border-b border-black pb-1 font-medium">
              {new Date().toLocaleDateString()}
            </div>
          </div>
        </div>

        {/* Insurer Details */}
        {session?.acceptData?.insurers && (
          <div className="mb-12">
            <h2 className="font-bold text-lg mb-4 border-b border-black pb-1">INSURER DETAILS</h2>
            {session.acceptData.insurers.map((insurer, index) => (
              <div key={index} className="mb-4 p-4 bg-gray-50 rounded-lg">
                <div className="flex justify-between items-center mb-2">
                  <span className="font-semibold">{insurer.name}</span>
                  <span className="text-sm bg-blue-100 px-2 py-1 rounded">Ref: {insurer.insurerQuotationNumber}</span>
                </div>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>Premium: {formatCurrency(insurer.premium)}</div>
                  <div>Broker Fee: {formatCurrency(insurer.brokerFee)}</div>
                  <div>Management Fee: {formatCurrency(insurer.managementFee)}</div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Client Details */}
        {client && (
          <div className="mb-12">
            <h2 className="font-bold text-lg mb-4 border-b border-black pb-1">CLIENT DETAILS</h2>
            <div className="grid grid-cols-2 gap-x-16 gap-y-3 text-sm">
              <div>Client Name:</div>
              <div className="border-b border-black pb-1">{client.name} {client.surname}</div>
              <div>Identification Number:</div>
              <div className="border-b border-black pb-1">{client.id_passport_number || '—'}</div>
              <div>Email:</div>
              <div className="border-b border-black pb-1 break-all">{client.email}</div>
              <div>Mobile Number:</div>
              <div className="border-b border-black pb-1">{client.mobile_number || '—'}</div>
            </div>
            {address && (
              <div className="mt-4">
                <div className="text-sm mb-1">Address:</div>
                <div className="border-b border-black pb-1">
                  {address.address_line_1} {address.address_line_2 && `, ${address.address_line_2}`}
                  {address.suburb && `, ${address.suburb}`}
                  {address.city && `, ${address.city}`}
                  {address.postal_code && `, ${address.postal_code}`}
                </div>
              </div>
            )}
          </div>
        )}

        {/* VEHICLES SECTION - Same as ProposalApplicationForm */}
        {vehicles.length > 0 && (
          <div className="mb-12">
            <h2 className="font-bold text-lg mb-4 border-b border-black pb-1">VEHICLES</h2>
            {vehicles.map((vehicle, index) => (
              <div key={vehicle.id || index} className="mb-8 last:mb-0">
                {index > 0 && <hr className="my-6 border-gray-300" />}
                
                <div className="space-y-6">
                  {/* Vehicle Information */}
                  <div className="space-y-3 text-sm">
                    <h3 className="font-bold text-gray-800 text-base mb-3">Vehicle Information</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="font-medium text-gray-700">Vehicle Type:</div>
                      <div className="text-gray-800">{vehicle.vehicle_type || '—'}</div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="font-medium text-gray-700">Registration Number:</div>
                      <div className="text-gray-800">{vehicle.registration_number || '—'}</div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="font-medium text-gray-700">Make:</div>
                      <div className="text-gray-800">{vehicle.make || '—'}</div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="font-medium text-gray-700">Model:</div>
                      <div className="text-gray-800">{vehicle.model || '—'}</div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="font-medium text-gray-700">Year:</div>
                      <div className="text-gray-800">{vehicle.year || '—'}</div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="font-medium text-gray-700">Variant:</div>
                      <div className="text-gray-800">{vehicle.variant || '—'}</div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="font-medium text-gray-700">Color:</div>
                      <div className="text-gray-800">{vehicle.color || '—'}</div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="font-medium text-gray-700">Vehicle Usage:</div>
                      <div className="text-gray-800">{vehicle.usage || '—'}</div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="font-medium text-gray-700">VIN Number:</div>
                      <div className="text-gray-800">{vehicle.vin_number || '—'}</div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="font-medium text-gray-700">Engine Number:</div>
                      <div className="text-gray-800">{vehicle.engine_number || '—'}</div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="font-medium text-gray-700">MM Code:</div>
                      <div className="text-gray-800">{vehicle.mmcode || '—'}</div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="font-medium text-gray-700">Cover Type:</div>
                      <div className="text-gray-800">{getCoverTypeDisplay(vehicle.cover_type)}</div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="font-medium text-gray-700">Insured Value:</div>
                      <div className="text-gray-800">{formatCurrency(vehicle.insured_value)}</div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="font-medium text-gray-700">No Claim Bonus:</div>
                      <div className="text-gray-800">{getNoClaimBonusDisplay(vehicle.no_claim_bonus)}</div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="font-medium text-gray-700">Use Retail Value:</div>
                      <div className="text-gray-800">{vehicle.use_retail_value ? 'Yes' : 'No'}</div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="font-medium text-gray-700">Excess Buy Back:</div>
                      <div className="text-gray-800">{vehicle.excess_buy_back ? 'Yes' : 'No'}</div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="font-medium text-gray-700">Credit Shortfall:</div>
                      <div className="text-gray-800">{vehicle.credit_shortfall ? 'Yes' : 'No'}</div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="font-medium text-gray-700">Car Hire:</div>
                      <div className="text-gray-800">{vehicle.car_hire ? 'Yes' : 'No'}</div>
                    </div>
                  </div>

                  {/* Address Details */}
                  <div className="space-y-3 text-sm">
                    <h3 className="font-bold text-gray-800 text-base mb-3">Address Details</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="font-medium text-gray-700">Address:</div>
                      <div className="text-gray-800">
                        {vehicle.address_line1} {vehicle.address_line2 ? `, ${vehicle.address_line2}` : ''}
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="font-medium text-gray-700">City/Town:</div>
                      <div className="text-gray-800">{vehicle.city_town || '—'}</div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="font-medium text-gray-700">Suburb:</div>
                      <div className="text-gray-800">{vehicle.suburb || '—'}</div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="font-medium text-gray-700">Province:</div>
                      <div className="text-gray-800">{vehicle.region || '—'}</div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="font-medium text-gray-700">Postal Code:</div>
                      <div className="text-gray-800">{vehicle.postal_code || '—'}</div>
                    </div>
                  </div>

                  {/* Financial Details */}
                  <div className="space-y-3">
                    <h3 className="font-bold text-gray-800 text-base mb-3">Financial Details</h3>
                    <div className="grid grid-cols-4 gap-2 text-sm">
                      <div className="text-gray-700">Premium:</div>
                      <div className="text-gray-800">{formatCurrency(vehicle.premium)}</div>
                      <div className="text-gray-700">Excess:</div>
                      <div className="text-gray-800">{formatCurrency(vehicle.excess)}</div>
                    </div>
                  </div>

                  {/* Parking Locations */}
                  <div className="space-y-3 text-sm">
                    <h3 className="font-bold text-gray-800 text-base mb-3">Parking Locations</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="font-medium text-gray-700">Day Parking Location:</div>
                      <div className="text-gray-800">{getParkingLocationDisplay(vehicle.day_parking_location)}</div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="font-medium text-gray-700">Night Parking Location:</div>
                      <div className="text-gray-800">{getParkingLocationDisplay(vehicle.night_parking_location)}</div>
                    </div>
                  </div>

                  {/* Driver Information */}
                  <div className="space-y-3 text-sm">
                    <h3 className="font-bold text-gray-800 text-base mb-3">Driver Information</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="font-medium text-gray-700">Registered Owner:</div>
                      <div className="text-gray-800">
                        {(() => {
                          const name = getPersonName(vehicle.registered_owner, client, personsOfInterest);
                          const license = getDriverLicenseDisplay(vehicle.registered_owner, personsOfInterest);
                          return `${name}${license}`;
                        })()}
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="font-medium text-gray-700">Regular Driver:</div>
                      <div className="text-gray-800">
                        {(() => {
                          const name = getPersonName(vehicle.regular_driver, client, personsOfInterest);
                          const license = getDriverLicenseDisplay(vehicle.regular_driver, personsOfInterest);
                          return `${name}${license}`;
                        })()}
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="font-medium text-gray-700">Other Drivers:</div>
                      <div className="text-gray-800">
                        {(() => {
                          const drivers = vehicle.other_drivers;
                          if (!drivers) return 'None';
                          const driverList = Array.isArray(drivers) ? drivers : [drivers];
                          const names = driverList
                            .map((d: any) => {
                              const name = getPersonName(d, client, personsOfInterest);
                              const license = getDriverLicenseDisplay(d, personsOfInterest);
                              return `${name}${license}`;
                            })
                            .filter((name: string) => name && name !== '—');
                          return names.length > 0 ? names.join(', ') : 'None';
                        })()}
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="font-medium text-gray-700">Safety Features:</div>
                      <div className="text-gray-800">{getSafetyFeaturesDisplay(vehicle.safety_features)}</div>
                    </div>
                  </div>

                  {/* Overnight Parking */}
                  {vehicle.overnight_parking_elsewhere && (
                    <div className="space-y-3 text-sm">
                      <h3 className="font-bold text-gray-800 text-base mb-3">Alternative Overnight Parking Address</h3>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="font-medium text-gray-700">Address:</div>
                        <div className="text-gray-800">{vehicle.address_line1 || '—'}</div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="font-medium text-gray-700">City/Town:</div>
                        <div className="text-gray-800">{vehicle.city_town || '—'}</div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="font-medium text-gray-700">Postal Code:</div>
                        <div className="text-gray-800">{vehicle.postal_code || '—'}</div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Footer with Sign Button */}
        <div className="flex justify-between mt-8 pt-6 border-t border-black">
          <div></div>
          <div className="flex space-x-4">
            <button
              onClick={handleSignClick}
              disabled={isSubmitting}
              className="px-8 py-3 bg-black text-white rounded hover:bg-gray-800 transition-colors flex items-center gap-2 disabled:opacity-50"
            >
              {isSubmitting ? (
                <>
                  <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Processing...
                </>
              ) : (
                <>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                  </svg>
                  Accept & Sign
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Signature Modal */}
      <SignatureModal
        isOpen={isSignatureModalOpen}
        onClose={() => setIsSignatureModalOpen(false)}
        onSave={handleSignatureSave}
        clientName={session?.clientName || ''}
      />
    </div>
  );
};

export default PublicSignaturePage;